import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _omit from "lodash/omit";
import React, { useLayoutEffect, useState } from 'react';
import classNames from 'classnames';
import Lazy from 'react-lazyload';
import Image from '@nerdwallet/react-image';
import { IMAGE_PLACEHOLDERS } from '@nerdwallet/marketplace/constants';
import styles from './marketplace-product-card.module.less';
import { getSrcWithDimensions } from './lib/utils';

var ProductCardImageWrapper = function ProductCardImageWrapper(_ref) {
  var backgroundColor = _ref.backgroundColor,
      _ref$className = _ref.className,
      className = _ref$className === void 0 ? '' : _ref$className,
      _ref$imageClassName = _ref.imageClassName,
      imageClassName = _ref$imageClassName === void 0 ? '' : _ref$imageClassName,
      imageData = _ref.imageData,
      _ref$isKetoCard = _ref.isKetoCard,
      isKetoCard = _ref$isKetoCard === void 0 ? false : _ref$isKetoCard,
      _ref$lazyLoadEnabled = _ref.lazyLoadEnabled,
      lazyLoadEnabled = _ref$lazyLoadEnabled === void 0 ? false : _ref$lazyLoadEnabled,
      objectFit = _ref.objectFit,
      _ref$imageOverlay = _ref.imageOverlay,
      imageOverlay = _ref$imageOverlay === void 0 ? null : _ref$imageOverlay,
      _ref$shouldDisplayPla = _ref.shouldDisplayPlaceHolder,
      shouldDisplayPlaceHolder = _ref$shouldDisplayPla === void 0 ? false : _ref$shouldDisplayPla;
  var alt = imageData.alt,
      breakpoint = imageData.breakpoint,
      imageDimensions = imageData.imageDimensions,
      imageDimensionsMobile = imageData.imageDimensionsMobile,
      src = imageData.src,
      srcSet = imageData.srcSet,
      sizes = imageData.sizes,
      imageRawDimensions = imageData.imageRawDimensions;
  /**
   * @type {[Array, Function]} Window dimensions
   */

  var _useState = useState([0, 0]),
      _useState2 = _slicedToArray(_useState, 2),
      size = _useState2[0],
      setSize = _useState2[1];

  useLayoutEffect(function () {
    var updateSize = function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    };

    window.addEventListener('resize', updateSize);
    updateSize();
    return function () {
      return window.removeEventListener('resize', updateSize);
    };
  }, []);

  var _size = _slicedToArray(size, 1),
      width = _size[0];

  var naturalHeight = imageDimensions === null || imageDimensions === void 0 ? void 0 : imageDimensions.height;
  var naturalWidth = imageDimensions === null || imageDimensions === void 0 ? void 0 : imageDimensions.width; // adjust lazy load placeholder height according to screen width

  if (width <= breakpoint && width !== 0) {
    naturalHeight = imageDimensionsMobile === null || imageDimensionsMobile === void 0 ? void 0 : imageDimensionsMobile.height;
    naturalWidth = imageDimensionsMobile === null || imageDimensionsMobile === void 0 ? void 0 : imageDimensionsMobile.width;
  }

  var imageProps = _omit(imageData, ['breakpoint', 'imageDimensions', 'imageDimensionsMobile']);

  var loadingImage = /*#__PURE__*/React.createElement("img", {
    alt: alt,
    className: imageClassName,
    src: IMAGE_PLACEHOLDERS.credit_cards,
    onError: function onError() {},
    sizes: sizes
  });
  var placeholder = shouldDisplayPlaceHolder ? loadingImage : /*#__PURE__*/React.createElement("div", null);
  var lazyImage = /*#__PURE__*/React.createElement(Lazy, {
    offset: 100,
    placeholder: placeholder
  }, /*#__PURE__*/React.createElement("img", {
    alt: alt,
    className: imageClassName,
    src: getSrcWithDimensions({
      src: src,
      imageRawDimensions: imageRawDimensions
    }),
    srcSet: getSrcWithDimensions({
      src: srcSet,
      imageRawDimensions: imageRawDimensions
    }),
    sizes: sizes
  }));
  /* eslint-disable no-nested-ternary */

  var image = lazyLoadEnabled && naturalHeight && naturalWidth ? isKetoCard ? /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.lazyImageWrapper])
  }, lazyImage) : /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.lazyImageWrapper, className]),
    style: {
      height: naturalHeight,
      width: naturalWidth
    }
  }, lazyImage) : /*#__PURE__*/React.createElement(Image, _extends({
    backgroundColor: backgroundColor,
    className: className,
    imageClassName: imageClassName,
    objectFit: objectFit
  }, imageProps));
  /* eslint-enable no-nested-ternary */

  return imageOverlay ? /*#__PURE__*/React.createElement("div", {
    className: styles.imageOverlayContainer
  }, image, /*#__PURE__*/React.createElement("div", {
    className: classNames([styles.imageOverlay])
  }, imageOverlay)) : image;
};

if (process.env.NODE_ENV !== 'production') {
  // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
  var PropTypes = require('prop-types');

  ProductCardImageWrapper.propTypes = {
    /**
     * Prop passed into react-image.
     */
    backgroundColor: PropTypes.string,

    /**
     * Styling for the image container.
     */
    className: PropTypes.string,

    /**
     * Styling for the image.
     */
    imageClassName: PropTypes.string,
    imageData: PropTypes.shape({
      alt: PropTypes.string,

      /**
       * The window size when we switch from
       * the desktop image to the mobile one
       */
      breakpoint: PropTypes.number,

      /**
       * Dimensions of desktop card image.
       */
      imageDimensions: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
      }),

      /**
       * Dimensions of the mobile card image.
       */
      imageDimensionsMobile: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
      }),
      src: PropTypes.string,
      srcSet: PropTypes.string,
      sizes: PropTypes.string,
      imageRawDimensions: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number
      })
    }),

    /**
     * To enable or disable lazyloading of images.
     */
    lazyLoadEnabled: PropTypes.bool,

    /**
     * Prop passed into react-image.
     */
    objectFit: PropTypes.string,
    isKetoCard: PropTypes.bool,
    imageOverlay: PropTypes.node
  };
}

export default ProductCardImageWrapper;